/**
 * URL Parameter Parser
 * Phase 200.9: Pure modular system loading
 * 
 * Extracts and validates URL query parameters for system loading:
 * - ?load={id} - System ID from manifest
 * - ?molxyz={base64} - Inline XYZ content (base64 encoded)
 * - ?smiles={string} - SMILES chemical notation
 */

import type { URLParams } from './types';
import { isValidSMILES } from './smilesParser';

/**
 * Decode base64 string to UTF-8
 * Copied from lib/base64.ts for zero legacy dependencies
 */
function base64DecodeUtf8(base64: string): string {
  try {
    // Use native atob if available (browser)
    if (typeof atob !== 'undefined') {
      const binary = atob(base64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
      }
      return new TextDecoder('utf-8').decode(bytes);
    }
    
    // Fallback for Node.js environment (testing)
    if (typeof Buffer !== 'undefined') {
      return Buffer.from(base64, 'base64').toString('utf-8');
    }
    
    throw new Error('No base64 decoder available');
  } catch (err) {
    throw new Error(`Failed to decode base64: ${(err as Error).message}`);
  }
}

/**
 * Encode UTF-8 text to base64 — the inverse of {@link base64DecodeUtf8}, so
 * `getInlineXYZ` round-trips a value written by `?molxyz=`. Exported for the
 * sandbox URL sync.
 */
export function base64EncodeUtf8(text: string): string {
  const bytes = new TextEncoder().encode(text);
  // Native btoa (browser) operates on binary strings.
  if (typeof btoa !== 'undefined') {
    let binary = '';
    for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
    return btoa(binary);
  }
  // Node.js fallback (testing).
  if (typeof Buffer !== 'undefined') {
    return Buffer.from(text, 'utf-8').toString('base64');
  }
  throw new Error('No base64 encoder available');
}

/**
 * Get window object (supports custom window for testing)
 */
function getWindow(win?: Window | null): Window | null {
  if (win) return win;
  
  // Try global window
  if (typeof window !== 'undefined') return window;
  
  // Try globalThis.window
  if (typeof globalThis !== 'undefined' && (globalThis as any).window) {
    return (globalThis as any).window as Window;
  }
  
  return null;
}

/**
 * Extract system ID from URL (?load={id})
 */
export function getSystemId(win?: Window | null): string | null {
  try {
    const w = getWindow(win);
    if (!w || !w.location) return null;
    
    const params = new URLSearchParams(w.location.search || '');
    const load = params.get('load');
    
    if (!load || typeof load !== 'string') return null;
    
    return load.trim();
  } catch {
    return null;
  }
}

/**
 * Extract and decode inline XYZ from URL (?molxyz={base64})
 * Returns decoded XYZ text, not base64
 */
function getInlineXYZ(win?: Window | null): string | null {
  try {
    const w = getWindow(win);
    if (!w || !w.location) return null;
    
    const params = new URLSearchParams(w.location.search || '');
    const b64 = params.get('molxyz');
    
    if (!b64 || typeof b64 !== 'string') return null;
    
    // Decode base64 to XYZ text
    return base64DecodeUtf8(b64.trim());
  } catch (err) {
    console.error('[urlParser] Failed to decode molxyz parameter:', err);
    return null;
  }
}

/**
 * Extract SMILES string from URL (?smiles={string})
 */
function getSMILES(win?: Window | null): string | null {
  try {
    const w = getWindow(win);
    if (!w || !w.location) return null;
    
    const params = new URLSearchParams(w.location.search || '');
    const smiles = params.get('smiles');
    
    if (!smiles || typeof smiles !== 'string') return null;
    
    const trimmed = smiles.trim();
    
    // Validate SMILES format
    if (!isValidSMILES(trimmed)) {
      console.warn('[urlParser] Invalid SMILES format:', trimmed);
      return null;
    }
    
    return trimmed;
  } catch {
    return null;
  }
}

/**
 * Parse all URL parameters at once
 */
export function parseURLParams(win?: Window | null): URLParams {
  return {
    load: getSystemId(win) ?? undefined,
    molxyz: getInlineXYZ(win) ?? undefined,
    smiles: getSMILES(win) ?? undefined,
  };
}
